import { useMemo } from 'react';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { detailCta } from '~/ui-rocco/pages/detail-shared';
import {
    DIRECTORY_GUTTER_X,
    EditorialArticleGrid,
} from './EditorialArticleGrid';
import { EditorialFeatureRow } from './EditorialFeatureRow';
import type { ArchizeenTile } from '../util/map-archizeen-tile-to-editorial';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';

/** One turn of the grid's rhythm: ten cards across three rows. */
const RHYTHM = 10;

/** The row of equals that closes a section, under the wide row. */
const CLOSING_ROW = 4;

export interface EditorialCategory {
    title: string;
    /** The category's own page, where Load more goes. */
    link: string;
    /** The category's articles in publish order, newest first. */
    articles: ArchizeenTile[];
}

interface EditorialCategorySectionProps {
    category: EditorialCategory;
    /**
     * Drop the heading. For the section that opens the page, where the tab bar
     * above already names the category and repeating it a line later only
     * pushes the first row of cards down.
     */
    hideHeading?: boolean;
    className?: string;
    /** The cards without their standfirst: picture, eyebrow and title only. */
    hideStandfirst?: boolean;
    /** Pictures graded onto the editorial palette. */
    graded?: boolean;
}

/**
 * One category, given a heading and a turn of the grid.
 *
 * The page is built from these rather than from one flat listing, so the
 * sections and the tabs above them are the same set of categories: change what
 * the CMS shows in the nav and the page's sections follow.
 *
 * Each shows a single turn — three rows of cards and the wide row after them —
 * and then hands the reader to the category's own page. Load more navigates
 * rather than appending: there are several sections on this page, and growing
 * one of them in place would push the others out of reach.
 */
export const EditorialCategorySection = ({
    category,
    hideHeading = false,
    className,
    hideStandfirst,
    graded,
}: EditorialCategorySectionProps) => {
    const { cards, feature, closing } = useMemo(() => {
        const tiles = category.articles.map((article) =>
            mapArchizeenTileToEditorial(article, { variant: 'editorialLarge' }),
        );
        return {
            cards: tiles.slice(0, RHYTHM),
            feature: tiles[RHYTHM],
            closing: tiles.slice(RHYTHM + 1, RHYTHM + 1 + CLOSING_ROW),
        };
    }, [category.articles]);

    if (cards.length === 0) return null;

    return (
        <section
            {...(hideHeading
                ? { 'aria-label': category.title }
                : { 'aria-labelledby': `category-${category.link}` })}
            className={className}
        >
            {hideHeading ? null : (
                <div className={DIRECTORY_GUTTER_X}>
                    <h2
                        id={`category-${category.link}`}
                        className="m-0 text-center font-gestura text-[3.75rem] leading-[1.1] font-normal tracking-[-0.03em] text-[#1f1f21] max-md:text-[2rem]"
                    >
                        {category.title}
                    </h2>
                </div>
            )}

            <EditorialArticleGrid
                articles={cards}
                layout="directory"
                hideStandfirst={hideStandfirst}
                graded={graded}
                className={hideHeading ? undefined : 'mt-10 md:mt-[3.75rem]'}
            />

            {feature ? (
                <EditorialFeatureRow
                    article={feature}
                    className="mt-10 md:mt-[5rem]"
                />
            ) : null}

            {closing.length > 0 ? (
                <EditorialArticleGrid
                    articles={closing}
                    layout="directory"
                    hideStandfirst={hideStandfirst}
                    graded={graded}
                    // A row of equals: the rhythm would put a double first,
                    // which after the wide row above reads as a new turn
                    // beginning rather than this one ending.
                    uniform
                    className="mt-10 md:mt-[5rem]"
                />
            ) : null}

            <div
                className={cn(
                    'mt-10 flex justify-center md:mt-[3.75rem]',
                    DIRECTORY_GUTTER_X,
                )}
            >
                <RoccoLink
                    to={category.link}
                    prefetch="intent"
                    // The page's white action, centred: it closes the section
                    // rather than sitting at the start of a line of content.
                    className={detailCta('white-lifted')}
                >
                    View all in {category.title}
                </RoccoLink>
            </div>
        </section>
    );
};
